import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Clock, Share2, Linkedin, Twitter, Facebook, Copy, Check } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import Chatbot from '../components/Chatbot';
import SEOHead from '../components/SEOHead';
import ReactMarkdown from 'react-markdown';
import { apiFetch } from '../lib/apiClient';
import { scrollTo, scrollToSection } from '../utils/smoothScroll';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const NICOLA_HERO_IMAGE = '/media/nicola-hero.webp';

const ArticlePage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Reset state and scroll to top when article ID changes
  useEffect(() => {
    setLoading(true);
    setArticle(null);
    setRelatedArticles([]);
    scrollTo(0, { immediate: true });
  }, [id]);

  useEffect(() => {
    const fetchArticle = async () => {
      try {
        const cacheBuster = `_t=${Date.now()}`;
        const response = await apiFetch(`${API_URL}/api/news/${id}?${cacheBuster}`, {
          headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
        });
        if (response.ok) {
          const data = await response.json();
          setArticle(data);
          
          // Fetch related articles
          const allResponse = await apiFetch(`${API_URL}/api/news?${cacheBuster}`, {
            headers: { 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }
          });
          if (allResponse.ok) {
            const allData = await allResponse.json();
            const related = allData
              .filter(a => a.id !== id)
              .slice(0, 3);
            setRelatedArticles(related);
          }
        } else {
          navigate('/insight');
        }
      } catch (error) {
        console.error('Error fetching article:', error);
        navigate('/insight');
      } finally {
        setLoading(false);
      }
    };
    
    fetchArticle();
  }, [id, navigate]);

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = article?.title || '';
    
    const shareUrls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#f8fafc]">
        <Navbar />
        <div className="flex items-center justify-center h-[60vh]">
          <div className="text-center">
            <div className="w-8 h-8 border-2 border-[#d4883a] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <p className="text-gray-500">Caricamento articolo...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return null;
  }

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <SEOHead 
        page="insight" 
        customTitle={`${article.title} | Nicola Scerni`}
        customDescription={article.excerpt}
        article={article}
      />
      <Navbar />
      
      {/* Hero */}
      <section className="bg-[#0b114e] pt-32 pb-16 sm:pt-40 sm:pb-20">
        <div className="max-w-[900px] mx-auto px-4 sm:px-6">
          <Link 
            to="/insight"
            className="inline-flex items-center gap-2 text-white/60 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Tutti gli articoli
          </Link>
          
          {article.category && (
            <div className="mt-6 mb-4">
              <span className="inline-block bg-[#d4883a] text-white text-xs font-semibold px-3 py-1 rounded-full">
                {article.category}
              </span>
            </div>
          )}
          
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight"
            style={{ fontFamily: 'Georgia, serif' }}
          >
            {article.title}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-white/60 text-sm">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {article.read_time || '5 min di lettura'}
            </span>
            {article.created_at && (
              <span>
                {new Date(article.created_at).toLocaleDateString('it-IT', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric'
                })}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* Featured Image */}
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 -mt-8">
        <div className="rounded-2xl overflow-hidden shadow-2xl bg-gray-100">
          <img
            src={article.imageUrl || article.image_url || article.image || NICOLA_HERO_IMAGE}
            alt={article.title}
            className="w-full h-64 sm:h-96 object-cover opacity-0 translate-y-2 animate-cardReveal"
            style={{ objectPosition: '25% top', animationDelay: '150ms' }}
            onError={(e) => { e.target.src = NICOLA_HERO_IMAGE; }}
          />
        </div>
      </div>

      {/* Content */}
      <article className="py-12 sm:py-16">
        <div className="max-w-[700px] mx-auto px-4 sm:px-6">
          {/* Share buttons */}
          <div className="flex items-center gap-3 mb-10 pb-6 border-b border-gray-200">
            <span className="text-gray-500 text-sm flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Condividi:
            </span>
            <button
              onClick={() => handleShare('linkedin')}
              className="w-9 h-9 bg-[#0077b5] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on LinkedIn"
            >
              <Linkedin className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleShare('twitter')}
              className="w-9 h-9 bg-[#1da1f2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on Twitter"
            >
              <Twitter className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleShare('facebook')}
              className="w-9 h-9 bg-[#1877f2] text-white rounded-full flex items-center justify-center hover:opacity-80 transition-opacity"
              aria-label="Share on Facebook"
            >
              <Facebook className="w-4 h-4" />
            </button>
            <button
              onClick={handleCopyLink}
              className="w-9 h-9 bg-gray-200 text-gray-600 rounded-full flex items-center justify-center hover:bg-gray-300 transition-colors"
              aria-label="Copy link"
            >
              {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
            </button>
          </div>

          {/* Main content */}
          <div className="article-content">
            {article.content ? (
              <ReactMarkdown
                components={{
                  h1: ({node, ...props}) => <h1 className="text-3xl font-bold text-[#0b114e] mt-8 mb-4" {...props} />,
                  h2: ({node, ...props}) => <h2 className="text-2xl font-bold text-[#0b114e] mt-8 mb-4" {...props} />,
                  h3: ({node, ...props}) => <h3 className="text-xl font-semibold text-[#0b114e] mt-6 mb-3" {...props} />,
                  p: ({node, ...props}) => <p className="text-gray-600 leading-relaxed mb-4" {...props} />,
                  ul: ({node, ...props}) => <ul className="list-disc pl-6 mb-4 text-gray-600" {...props} />,
                  ol: ({node, ...props}) => <ol className="list-decimal pl-6 mb-4 text-gray-600" {...props} />,
                  li: ({node, ...props}) => <li className="mb-2" {...props} />,
                  strong: ({node, ...props}) => <strong className="font-semibold text-gray-800" {...props} />,
                }}
              >
                {article.content}
              </ReactMarkdown>
            ) : (
              <p className="text-gray-600 leading-relaxed">
                {article.excerpt}
              </p>
            )}
          </div>

          {/* Author Box */}
          <div className="mt-12 p-6 sm:p-8 bg-[#0b114e] rounded-2xl">
            <div className="flex items-start gap-4">
              <img
                src={NICOLA_HERO_IMAGE}
                alt="Nicola Scerni"
                className="w-16 h-16 min-w-[64px] rounded-full object-cover flex-shrink-0"
                style={{ objectPosition: '25% top' }}
              />
              <div>
                <h3 className="text-white font-bold text-lg">Nicola Scerni</h3>
                <p className="text-white/60 text-sm mb-3">Consulente di Marketing Strategico</p>
                <p className="text-white/70 text-sm leading-relaxed">
                  Aiuto imprenditori e CEO a costruire strategie di marketing che trasformano il posizionamento e i risultati delle loro aziende.
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="mt-12 text-center p-8 bg-gradient-to-br from-[#d4883a]/10 to-[#0b114e]/10 rounded-2xl border border-[#d4883a]/20">
            <h3 className="text-2xl font-bold text-[#0b114e] mb-3" style={{ fontFamily: 'Georgia, serif' }}>
              Vuoi risultati simili?
            </h3>
            <p className="text-gray-600 mb-6">
              Scopri se possiamo lavorare insieme per trasformare il tuo marketing.
            </p>
            <button
              onClick={() => {
                scrollToSection('contact');
              }}
              className="inline-flex items-center gap-2 bg-[#d4883a] hover:bg-[#d4896a] text-white font-semibold px-6 py-3 rounded-full transition-colors"
            >
              Richiedi una Consulenza
            </button>
          </div>
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <section className="py-12 sm:py-16 bg-white">
          <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-[#0b114e] mb-8" style={{ fontFamily: 'Georgia, serif' }}>
              Altri articoli
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {relatedArticles.map((related, index) => (
                <Link
                  key={related.id || index}
                  to={`/insight/${related.id}`}
                  className="group bg-gray-50 rounded-xl overflow-hidden hover:shadow-lg transition-all"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={related.imageUrl || related.image_url || related.image || NICOLA_HERO_IMAGE}
                      alt={related.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      style={{ objectPosition: '25% top' }}
                      onError={(e) => { e.target.src = NICOLA_HERO_IMAGE; }}
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[#0b114e] line-clamp-2 group-hover:text-[#d4883a] transition-colors">
                      {related.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
      <Chatbot />
    </div>
  );
};

export default ArticlePage;
